import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Button, SidebarSelection } from '@trucktrax/trucktrax-common';
import { ProductLine, TicketDto, VoidFunc } from '@trucktrax/trucktrax-ts-common';
import cx from 'classnames';
import { resolveTicketProductLine } from '../../../services/ticketProductLineService';
import {
  clearSelectedTicket,
  resetPastTicketDates,
  resetTicketView,
  updateTicketTimeline,
  isOpenTicket,
  needToRefreshData,
} from '../../../store/actions/ticketActions';
import setSidebarSelected from '../../../store/actions/sidebarActions';
import {
  INCOMING_MATERIALS_ROUTE,
  INCOMING_TICKETS_SELECTED,
} from '../../../constants/appConstants';
import { TicketStateSwitcherType } from '../../../types';
import { NavItem } from '../../../constants/navConstants';
import { ReduxState } from '../../../store';
import { toggleMapFilter } from '../../../store/actions/mapFilterActions';
import {
  incomingTicketProductLineFilterSelector,
  incomingTicketsForTicketPanelSelector,
  selectedIncomingPlantUrlsSelector,
} from '../../../store/selectors/incomingMaterialsSelectors';
import Ticket from '../../shared/tickets/details/Ticket';
import TicketList from '../../shared/tickets/TicketList';
import incomingCurrentTickets from '../../shared/tickets/ticketListConfigurations/incomingCurrentTickets';
import incomingPastTickets from '../../shared/tickets/ticketListConfigurations/incomingPastTickets';
import { incomingMaterialsSearchText } from '../../shared/tickets/ticketListConfigurations/ticketColumnAccessors';
import IncomingProductLineFilter from './IncomingProductLineFilter';
import styles from './IncomingTicketsPopover.module.scss';

interface IncomingTicketsPopoverProps {
  setSidebarSelected?: (sidebarTitle: string, isSelected: boolean) => void;
  clearSelectedTicket: VoidFunc;
  selectedTicket?: TicketDto;
  sidebarSelection?: SidebarSelection;
  updateTicketTimeline: (timeline: string) => void;
  resetPastTicketDates: VoidFunc;
  resetTicketView: VoidFunc;
  history: { push: (path: string) => void };
  isOpenTicket: (isOpenTicket: boolean) => void;
  needToRefreshData: (needToRefreshData: boolean) => void;
  geotraxPage?: NavItem;
  destinationPlantUrls: string[];
  incomingTickets: TicketDto[];
  /** DEV-5225: product lines unchecked in the Product Line filter. */
  incomingDeselectedProductLines: ProductLine[];
  /** DEV-5225: the product lines the filter keeps; undefined when it applies no filter. */
  productLineFilter?: ProductLine[];
  toggleMapFilter: typeof toggleMapFilter;
  /** DEV-3924: resolves the ticket's origination-plant product line (connected thunk). */
  resolveTicketProductLine: (ticket: TicketDto) => Promise<ProductLine | undefined>;
}

interface IncomingTicketsPopoverState {
  isExpanded: boolean;
  isTicketsOpen: boolean;
  /** DEV-3924: ticket-derived product line; null while unresolved (no status tree). */
  resolvedProductLine: ProductLine | null;
}

export class IncomingTicketsPopover extends Component<
  IncomingTicketsPopoverProps,
  IncomingTicketsPopoverState
> {
  // Guards the setState that follows the awaited product-line resolution against
  // landing after the popover is unmounted mid-flight.
  private hasUnmounted = false;

  constructor(props: IncomingTicketsPopoverProps) {
    super(props);
    this.state = {
      isExpanded: false,
      isTicketsOpen: false,
      resolvedProductLine: null,
    };
  }

  componentDidMount() {
    this.updateTicketsOpen();
    this.resolveProductLineForSelectedTicket();
  }

  componentDidUpdate(prevProps: IncomingTicketsPopoverProps) {
    if (prevProps.sidebarSelection !== this.props.sidebarSelection) {
      this.updateTicketsOpen();
    }

    if (prevProps.selectedTicket?.id !== this.props.selectedTicket?.id) {
      this.resolveProductLineForSelectedTicket();
    }

    // Close popover when navigating away from incoming materials page
    const wasOnIncoming = prevProps.geotraxPage?.navRoute?.includes(INCOMING_MATERIALS_ROUTE);
    const isOnIncoming = this.props.geotraxPage?.navRoute?.includes(INCOMING_MATERIALS_ROUTE);
    if (wasOnIncoming && !isOnIncoming && this.state.isTicketsOpen) {
      this.closePopover();
    }
  }

  componentWillUnmount() {
    this.hasUnmounted = true;
  }

  // DEV-3924: the status tree must come from the ticket's origination plant, never the
  // leftover main-map selectedProductLine. While unresolved (in flight or failed) the
  // override stays null and the Ticket view renders no status tree.
  resolveProductLineForSelectedTicket = async () => {
    const ticket = this.props.selectedTicket;
    if (!ticket?.id) {
      return;
    }

    this.setState({ resolvedProductLine: null });
    const resolved = await this.props.resolveTicketProductLine(ticket);

    // Stale guard: a slow resolution must not land after unmount or clobber a newer
    // ticket's result.
    if (!this.hasUnmounted && this.props.selectedTicket?.id === ticket.id) {
      this.setState({ resolvedProductLine: resolved ?? null });
    }
  };

  updateTicketsOpen = () => {
    const isIncomingTicketsSidebar =
      this.props.sidebarSelection?.sidebarTitle === INCOMING_TICKETS_SELECTED;
    const hasChanged =
      isIncomingTicketsSidebar &&
      this.props.sidebarSelection!.isSelected !== this.state.isTicketsOpen;

    if (hasChanged) {
      this.setState({
        isTicketsOpen: this.props.sidebarSelection!.isSelected,
      });
    }
  };

  closePopover = () => {
    this.props.setSidebarSelected!(INCOMING_TICKETS_SELECTED, false);
    this.props.resetPastTicketDates();
    this.props.resetTicketView();
    this.props.updateTicketTimeline('current');
    this.props.isOpenTicket(false);

    global.setTimeout(() => {
      this.props.clearSelectedTicket();
      this.setState({ isExpanded: false });
    }, 500);
  };

  handleClosePopover = () => {
    this.props.needToRefreshData(false);
    this.closePopover();
  };

  openTicketList = () => {
    this.props.clearSelectedTicket();
    this.props.resetTicketView();
  };

  expandClick = () => {
    this.setState(prev => ({ isExpanded: !prev.isExpanded }));
  };

  // DEV-5225: saved with the Destination Plants selection by MapFiltersDropdown's incoming save.
  // Marked edited so it is saved even before the saved selection has been verified.
  onProductLineFilterChange = (incomingDeselectedProductLines: ProductLine[]) => {
    this.props.toggleMapFilter({
      incomingDeselectedProductLines,
      incomingProductLinesEdited: true,
      shouldSave: true,
    });
  };

  outerContainerClickHandler = (event: any) => {
    const targetIsExpandedOuterContainer = Array.from(event.target.classList).includes(
      styles.containerExpanded,
    );
    if (targetIsExpandedOuterContainer) {
      this.closePopover();
    }
  };

  render() {
    const { selectedTicket } = this.props;
    const { isExpanded, isTicketsOpen } = this.state;

    const isOnIncomingMaterials =
      this.props.geotraxPage?.navRoute?.includes(INCOMING_MATERIALS_ROUTE);
    if (!isTicketsOpen || !isOnIncomingMaterials) {
      return null;
    }

    const selectedTicketNumber = selectedTicket?.ticketNumber
      ? `TICKET  #${selectedTicket.ticketNumber}`
      : '';
    const isSelected = !!selectedTicket?.ticketNumber;

    let renderItem = null;
    if (selectedTicket?.id) {
      renderItem = (
        <Ticket
          selectedTicket={selectedTicket}
          shrinkPopover={() => {
            if (isExpanded) this.expandClick();
          }}
          history={this.props.history}
          closePopover={this.closePopover}
          productLineOverride={this.state.resolvedProductLine}
          unresolvedDriverIsExternal
          isIncomingMaterials
        />
      );
    } else {
      renderItem = (
        <TicketList
          ticketStateSwitcherType={TicketStateSwitcherType.Tabs}
          currentColumnsOverride={incomingCurrentTickets}
          pastColumnsOverride={incomingPastTickets}
          defaultSortOverride={[{ id: 'ticketNumber', desc: true }]}
          useDestinationPlantsFilter
          destinationPlantUrls={this.props.destinationPlantUrls}
          activeTicketsOverride={this.props.incomingTickets}
          extraSearchText={incomingMaterialsSearchText}
          headerFilter={
            <IncomingProductLineFilter
              deselectedProductLines={this.props.incomingDeselectedProductLines}
              onChange={this.onProductLineFilterChange}
            />
          }
          extraQueryParams={
            this.props.productLineFilter
              ? { inProductLines: this.props.productLineFilter.join(',') }
              : undefined
          }
        />
      );
    }

    let containerStyle = styles.container;
    if (isExpanded) {
      containerStyle = styles.containerExpanded;
    }

    return (
      <div className={containerStyle} onClick={this.outerContainerClickHandler}>
        <div
          className={cx(styles.popover, {
            [styles.expandedPopover]: isExpanded,
          })}
        >
          <div className={styles.header}>
            <div className={styles.headerTitle}>
              <span
                onClick={this.openTicketList}
                data-test="incoming-tickets-breadcrumb"
                role="button"
                aria-hidden
              >
                {isSelected ? (
                  <i
                    aria-hidden="true"
                    className={cx(styles.backArrow, 'icon-arrow-back', 'icon-small')}
                  />
                ) : (
                  <span className={styles.titleText}>INCOMING MATERIAL TICKETS</span>
                )}
              </span>
              <span className={styles.ticketNumber}>{selectedTicketNumber}</span>
            </div>
            <div>
              <Button
                onClick={this.expandClick}
                buttonClassName={cx(styles.closeButton, 'tt-btn-basic')}
                iconClassName={cx(
                  {
                    'icon-expand': !isExpanded,
                    'icon-collapse': isExpanded,
                  },
                  'icon-white',
                )}
                dataTest="incoming-tickets-expand-button"
              />
              <Button
                onClick={this.handleClosePopover}
                buttonClassName={cx(styles.closeButton, 'tt-btn-basic')}
                iconClassName={cx('icon-close', 'icon-white')}
                dataTest="incoming-tickets-close-button"
              />
            </div>
          </div>
          {renderItem}
        </div>
      </div>
    );
  }
}

export function mapStateToProps(state: Partial<ReduxState>) {
  return {
    selectedTicket: state.selectedTicket,
    sidebarSelection: state.sidebar,
    isFromOpenTicket: state.isOpenTicket,
    geotraxPage: state.geotraxPage,
    destinationPlantUrls: selectedIncomingPlantUrlsSelector(state as ReduxState),
    incomingTickets: incomingTicketsForTicketPanelSelector(state as ReduxState),
    incomingDeselectedProductLines: state.mapFilters?.incomingDeselectedProductLines ?? [],
    productLineFilter: incomingTicketProductLineFilterSelector(state as ReduxState),
  };
}

export default connect<any, any, any>(mapStateToProps, {
  setSidebarSelected,
  clearSelectedTicket,
  updateTicketTimeline,
  resetPastTicketDates,
  resetTicketView,
  isOpenTicket,
  needToRefreshData,
  resolveTicketProductLine,
  toggleMapFilter,
})(IncomingTicketsPopover as any);
